import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type {
  FPlusExpressionValue,
  TemplateReference,
} from '../../types/templateModel'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import type { ExpressionTypeInformation } from '../fplusExpression/types'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import type { TemplateReferenceExpressionFields } from './types'

type Props = {
  data: TemplateReference
  onChange?: (nextValue: Optional<TemplateReference>) => Promise<void>
  fieldName: TemplateReferenceExpressionFields
  errors?: ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  typeInformation?: Optional<ExpressionTypeInformation>
}

export const TemplateReferenceFieldValueWithError = ({
  data,
  onChange,
  fieldName,
  errors,
  typeInformation,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()
  const handleChange = useCallback(
    (nextValue: FPlusExpressionValue) => {
      assertNotEmpty(templateVersion?.versionInfo)
      assertNotEmpty(onChange)

      return onChange({
        ...data,
        [fieldName]: {
          ...data[fieldName],
          origin: data[fieldName]?.path,
          expression: nextValue,
        },
      })
    },
    [data, fieldName, onChange, templateVersion?.versionInfo],
  )

  assertNotEmpty(templateVersion)

  const errorMessages = (errors ?? []).map(
    ({ error }) => error.errorMessage ?? translate('error.unknown'),
  )

  const fullPath = `${data.path}.${fieldName}`

  return (
    <div className="flex h-full flex-col gap-y-2">
      <TemplateFieldExpression
        expression={data[fieldName]?.expression}
        onChange={handleChange}
        path={fullPath}
        fallbackCode=""
        isExpanded
        key={fullPath}
        typeInformation={typeInformation}
        fromOrigin={data[fieldName]?.origin}
      />
      {errorMessages.map((message) => (
        <div
          className="whitespace-break-spaces break-all text-error-500"
          key={message}
        >
          {message || translate('error.unknown')}
        </div>
      ))}
    </div>
  )
}
